import { useReducer, useState, useEffect } from "react"
import { NumberInput } from 'carbon-components-react';
import Select from 'react-select'
// styles
import styled from "styled-components";
import { selectStyles } from './formUtils'

const useAgeField = (
  defaultAges,
  // useStudyData takes care of dirty fields
  dirtyFields,
  onChangeAge,
) => {

  const onlyAttachment = localStorage.getItem('only_attachment')
  
  const dateUnitOptions = [
    { value: "1", label: "days" },
    { value: "2", label: "months" },
    { value: "3", label: "years" },
  ]

  // SET DATA TO UPDATE

  /**
   * Get digit part of age object.
   * @param {Object} value in format:
   *   { [unit]: digit, text, calculated }
   */
  const getAgeDigit = (value) => {
    let digit = null
    if (value) {
      ['days', 'months', 'years'].forEach((unit) => {
        if (value.hasOwnProperty(unit)) {
          digit = value[unit];
        }
      })
    }
    return digit
  }

  /**
   * Get units part of age object.
   * @param {Object} value in format:
   *   { [unit]: digit, text, calculated }
   */
  const getAgeUnits = (value) => {
    if (value) {
      return ['days', 'months', 'years'].find((unit) => {
        if (value.hasOwnProperty(unit)) return unit
      })
    }
    return null
  }
  /**
   * Calculate max and min ages values
   * * action.id is "maximum_age" or "minimum_age"
   * * action.type is "digit" or "units"
   * * action.value is 50 or "years"
  */
  /**
   * Calculate max and min ages values
   * @param {Object} state reducer state
   * @param {Object} action
   * * action.id is "maximum_age" or "minimum_age"
   * * action.type is "digit" or "units"
   * * action.value is 50 or "years"
   * @returns object in formats:
   *   { maximum_age: { digit, units } }
   *   { minimum_age: { digit, units } }
   *   "digit" may not be returned if not selected by user
   */
  const agesReducer = (state, action) => {
    const id = action.id
    let newState = {}
    if (state) newState = { ...state }
    let units = null
    let digit = null
    if (action.type === 'digit') {
      units = getAgeUnits(state?.[id]) || 'days'
      digit = action.value
    }
    if (action.type === 'units') {
      digit = getAgeDigit(state?.[id])
      units = action.value
    }

    if (digit && Number(digit) !== 0) {
      // unitsText eg: "1 Year", "30 Months"
      let unitsText = null
      // calculated is number of days
      let calculated = null;
      if (units === 'days') {
        calculated = Number(digit)
        unitsText = Number(digit) === 1 ? 'Day' : 'Days'
      } else if (units === 'months') {
        unitsText = Number(digit) === 1 ? 'Month' : 'Months'
        calculated = digit * 30
      } else if (units === 'years') {
        unitsText = Number(digit) === 1 ? 'Year' : 'Years'
        calculated = digit * 356
      }
      const value = {
        [units]: digit,
        calculated,
        text: `${digit} ${unitsText}`,
      }
      newState[id] = value
      onChangeAge(id, value)
    } else {
      newState[id] = null
      onChangeAge(id, null)
    }
    return newState
  }

  const [ages, setAges] = useReducer(agesReducer, defaultAges)

  const onChangeAgeDigit = (evt, stepDirection) => {
    let id = null
    let value = null
    if (stepDirection) {
      id = evt.imaginaryTarget.id
      value = evt.imaginaryTarget.defaultValue
    } else {
      id = evt.target.id
      value = evt.target.value
    }
    setAges({ id, type: 'digit', value })
  }

  const onChangeAgeUnits = (selectItem, fieldId) => {
    const id = fieldId
    const value = selectItem.label
    setAges({ id, type: 'units', value })
  }

  const numberInput = (id, label) => {
    return <NumberInputStyled isDirty={dirtyFields?.[id]}>
      <NumberInput
        disabled={onlyAttachment === "true"}
        id={id}
        label={label}
        defaultValue={ages?.[id] && getAgeDigit(ages[id])}
        onChange={onChangeAgeDigit}
        // hideSteppers={true} // doesn't work
        allowEmpty={true}
        min={0}
      />
    </NumberInputStyled>
  }

  const unitsSelect = (id) => {
    return <Select
      name={`date_units-${id}`}
      aria-labelledby={`date_units-${id}`}
      defaultValue={(() => {
        let unit = 'days'
        if (ages?.[id]) unit = getAgeUnits(ages[id])
        // return select object: { value, label }
        return dateUnitOptions.find((option) => option.label === unit)
      })()}
      options={dateUnitOptions}
      isDisabled={onlyAttachment === "true"}
      onChange={(evt) => onChangeAgeUnits(evt, id)}
      styles={selectStyles({ isDirty: dirtyFields?.[id] })}
    />

  }

  return {
    // GET CURRENT DATA
    getAgeDigit,
    getAgeUnits,
    // SET DATA TO UPDATE
    numberInput,
    unitsSelect,
    ages,
  }
}

export default useAgeField

const NumberInputStyled = styled.div`
  .bx--number input[type='number'] {
    background-color: ${({ isDirty }) => isDirty && '#e6f7ff'};
  }
`